/**
 * views/Kitchen/KitchenAnalytics.js
 * ─────────────────────────────────────────────────────────────────────────────
 * The kitchen performance report. A DESK screen, so it wears the light
 * dashboard THEME rather than the dark KDS palette: this is read sitting down,
 * on a laptop, by someone deciding whether to hire another cook. The board
 * (dark, three metres away, hands full) is a different design problem and lives
 * in its own files.
 *
 * ## What this screen is allowed to claim
 *
 * services/kitchen/kitchenAnalyticsService.js states four measurement rules at
 * the top of the file. Every one of them constrains something visible here, so
 * they are repeated where they bite rather than left for the reader to guess:
 *
 * 1. Durations are BANKED, never re-derived. Nothing here recomputes a stage
 *    time from timestamps, and nothing offers to "fix" one.
 * 2. SLA is judged against the promise that was live on each ticket. The copy
 *    says so, because a reader who assumes today's promise will misread last
 *    month's breach count.
 * 3. Rejected and cancelled tickets are OUT of every timing metric and INTO
 *    the exceptions block. Each timing panel says which denominator it used.
 * 4. Percentiles come from a declared sample. When `distribution.sampled` is
 *    true the sampling note is rendered unconditionally. It is never hidden,
 *    never behind a tooltip, never conditional on hover.
 *
 * ## The stage chart is grouped, not stacked
 *
 * Worth stating loudly because the instinct is to stack it. In
 * kitchenStateMachine.stageTimestampPatch, `toAcknowledgeMs` and
 * `toPrepStartMs` are BOTH measured from `placedAt`, so they overlap:
 * toPrepStartMs already contains the acknowledge wait. `prepMs` is anchored at
 * `prepStartedAt` and `readyToHandoffMs` at `readyAt`. Stacking the four would
 * count the pre-cook wait twice and inflate the apparent kitchen time. Grouped
 * bars off a shared zero are the honest shape.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";

import { apiKitchenAnalytics, apiKitchenExport, apiKitchenList } from "../../api/index";
import { THEME } from "../../components/dashboardTheme";
import {
  KDS,
  STATUS_META,
  SLA_META,
  formatDuration,
  formatMinutes,
  formatClock,
  formatPercent,
  formatMoney,
  stationColour,
} from "./kitchenUi";
import AppSelect from "../../components/ui/AppSelect";
import { useBranchScope } from "../../context/BranchScopeContext";

// Registered here rather than globally, matching components/VendorSalesAnalytics.js.
// ArcElement is the only addition that file does not already pull in: it is what
// the SLA doughnut draws with, and without it chart.js throws at first render.
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

// Mongo's $dayOfWeek is 1-based and starts on Sunday. Index 0 is never read.
const DOW_LABELS = ["", "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const HOUR_LABELS = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);

/** Reading order for the SLA slices. Best to worst, so the eye lands on trouble last. */
const SLA_ORDER = ["ON_TIME", "AT_RISK", "LATE", "BREACHED"];

/**
 * The four stage averages, in the order a ticket meets them. `anchor` is the
 * point each one is measured FROM, and it is printed next to the bar because
 * two of these four share an anchor and the reader cannot tell by looking.
 */
const STAGE_FIELDS = [
  {
    key: "avgToAcknowledgeMs",
    label: "Order to accepted",
    anchor: "from the moment the order landed",
    colour: KDS.new,
  },
  {
    key: "avgToPrepStartMs",
    label: "Order to cooking started",
    anchor: "from the moment the order landed",
    colour: KDS.accepted,
  },
  {
    key: "avgPrepMs",
    label: "Cooking",
    anchor: "from the moment cooking started",
    colour: KDS.preparing,
  },
  {
    key: "avgReadyToHandoffMs",
    label: "Ready to handed over",
    anchor: "from the moment the food went up",
    colour: KDS.ready,
  },
];

const PERCENTILE_ROWS = [
  { key: "prepMs", label: "Cooking time" },
  { key: "totalKitchenMs", label: "Total time in the kitchen" },
  { key: "toAcknowledgeMs", label: "Order to accepted" },
];

const pad2 = (n) => String(n).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** The date inputs are local calendar days; the API wants instants. */
const dayStartIso = (value) => {
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};
const dayEndIso = (value) => {
  const d = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** A whole number is a whole number. Only rates and durations get decimals. */
const formatCount = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString() : "0");

/** Operator shifts span days, so the clock alone would be ambiguous. */
const formatDayAndClock = (value) => {
  if (!value) return "Not recorded";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "Not recorded";
  return `${d.toLocaleDateString(undefined, { day: "2-digit", month: "short" })} ${formatClock(d)}`;
};

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const S = {
  page: { background: THEME.bg, minHeight: "100%", padding: "0 0 32px" },
  header: {
    background: `linear-gradient(135deg, ${THEME.header}, ${THEME.headerTo})`,
    boxShadow: THEME.headerShadow,
    borderRadius: THEME.radius,
    color: "#fff",
    padding: "20px 24px",
    marginBottom: 18,
  },
  headerTitle: { margin: 0, fontSize: "1.35rem", fontWeight: 800, letterSpacing: "0.01em" },
  headerSub: { margin: "6px 0 0", fontSize: 13.5, color: "rgba(255,255,255,0.78)", maxWidth: 820 },
  panel: {
    background: THEME.panel,
    border: `1px solid ${THEME.border}`,
    borderRadius: THEME.radius,
    boxShadow: THEME.shadow,
    padding: "18px 20px",
    marginBottom: 18,
  },
  strip: {
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 11,
    fontWeight: 800,
    color: THEME.textLight,
    marginBottom: 4,
  },
  sectionTitle: { fontSize: "1.05rem", fontWeight: 700, color: THEME.text, margin: "0 0 4px" },
  note: { fontSize: 12.5, color: THEME.textMid, margin: "0 0 14px", lineHeight: 1.5, maxWidth: 880 },
  warnNote: {
    fontSize: 12.5,
    color: THEME.text,
    background: THEME.amberSoft,
    border: `1px solid ${THEME.amber}`,
    borderRadius: THEME.radiusSm,
    padding: "9px 12px",
    margin: "0 0 14px",
    lineHeight: 1.5,
  },
  filterRow: { display: "flex", flexWrap: "wrap", gap: 14, alignItems: "flex-end" },
  field: { display: "flex", flexDirection: "column", gap: 5, minWidth: 160 },
  label: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: THEME.textLight },
  input: {
    border: `1px solid ${THEME.border}`,
    borderRadius: THEME.radiusSm,
    padding: "8px 10px",
    fontSize: 13.5,
    color: THEME.text,
    background: "#fff",
    minHeight: 38,
  },
  button: (variant) => ({
    border: variant === "ghost" ? `1px solid ${THEME.border}` : "none",
    background: variant === "ghost" ? "#fff" : THEME.blue,
    color: variant === "ghost" ? THEME.textMid : "#fff",
    fontWeight: 700,
    fontSize: 13.5,
    borderRadius: THEME.radiusSm,
    padding: "9px 16px",
    minHeight: 38,
    cursor: "pointer",
  }),
  chip: {
    display: "inline-block",
    background: THEME.blueSoft,
    color: THEME.blueDeep,
    border: `1px solid ${THEME.blue}`,
    borderRadius: 999,
    padding: "3px 10px",
    fontSize: 11.5,
    fontWeight: 700,
  },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 },
  kpi: (accent) => ({
    background: THEME.stripBg,
    border: `1px solid ${THEME.border}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: THEME.radiusSm,
    padding: "13px 15px",
  }),
  kpiLabel: { fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: THEME.textLight },
  kpiValue: { fontSize: "1.5rem", fontWeight: 800, color: THEME.text, lineHeight: 1.15, margin: "6px 0 5px" },
  kpiSub: { fontSize: 11.5, color: THEME.textMid, lineHeight: 1.45 },
  two: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 18 },
  chartBox: { height: 250 },
  chartBoxSm: { height: 220 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    textAlign: "left",
    fontSize: 10.5,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: THEME.textLight,
    borderBottom: `1px solid ${THEME.border}`,
    padding: "8px 10px",
    whiteSpace: "nowrap",
  },
  td: { padding: "8px 10px", borderBottom: `1px solid ${THEME.stripBg}`, color: THEME.text, verticalAlign: "top" },
  tdNum: {
    padding: "8px 10px",
    borderBottom: `1px solid ${THEME.stripBg}`,
    color: THEME.text,
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  },
  swatch: (colour) => ({
    display: "inline-block",
    width: 10,
    height: 10,
    borderRadius: 3,
    background: colour,
    marginRight: 7,
    verticalAlign: "middle",
  }),
  legendRow: { display: "flex", flexWrap: "wrap", gap: 16, fontSize: 12, color: THEME.textMid, marginTop: 10 },
  empty: {
    background: THEME.stripBg,
    border: `1px dashed ${THEME.border}`,
    borderRadius: THEME.radiusSm,
    padding: "20px 16px",
    textAlign: "center",
    color: THEME.textLight,
    fontSize: 13,
  },
  scroll: { overflowX: "auto" },
};

const emptyNote = (text) => <div style={S.empty}>{text}</div>;

/**
 * @param {object} props
 * @param {Array<object>} [props.outlets] Optional outlet list for the location
 *   selector, shaped `{ _id, name }`. The Kitchen Display System API surface has
 *   no endpoint that lists outlets by name, and this screen is not allowed to
 *   reach outside it, so the parent screen (which already loaded them) passes
 *   them down. Without the prop the selector still offers the two options the
 *   backend understands on its own: every location, or the main store only.
 */
export default function KitchenAnalytics({ outlets = [] }) {
  const { search } = useLocation();

  /**
   * Lusuku staff arrive here from a vendor screen with `?vendorId=...` already
   * on the URL. It is a PASSTHROUGH, not a control: the server re-derives the
   * caller's real scope in resolveKitchenScope, so a restaurant operator who
   * hand-edits this parameter still only ever sees their own kitchen.
   */
  const vendorId = useMemo(() => new URLSearchParams(search).get("vendorId") || "", [search]);
  const history = useHistory();

  /**
   * The kitchen list serves two purposes here. It lets Lusuku staff scope the
   * report to one restaurant without hand-editing the address, and it supplies
   * the outlet names for the Location filter. Nothing else in the kitchen API
   * exposes outlets, which is why this screen previously accepted them as a
   * prop that no route could ever pass.
   */
  const [kitchens, setKitchens] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiKitchenList()
      .then(({ data }) => {
        if (!cancelled) setKitchens(data?.data?.kitchens || []);
      })
      .catch(() => {
        if (!cancelled) setKitchens([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedKitchen = useMemo(
    () => kitchens.find((k) => String(k.vendorId) === String(vendorId)) || null,
    [kitchens, vendorId]
  );

  // An explicitly-passed prop still wins, so a future parent screen that has
  // already loaded outlets does not pay for a second request.
  const resolvedOutlets = outlets.length ? outlets : selectedKitchen?.outlets || [];

  const initialRange = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - 6 * 24 * 60 * 60 * 1000);
    return { from: isoDay(start), to: isoDay(end) };
  }, []);

  const [from, setFrom] = useState(initialRange.from);
  const [to, setTo] = useState(initialRange.to);
  // Starts on the branch picked in the navbar (4 Oct 2026); staff and
  // vendors without branches start on every location, as before.
  const {
    outletIdParam,
    isBranchScoped,
    loaded: branchesLoaded,
    locations: myLocations,
  } = useBranchScope();
  const [outletId, setOutletId] = useState(() => outletIdParam || "");
  /* A member limited to several branches reads one branch at a time: the
     server refuses "all of them" with 400 BRANCH_LOCATION_REQUIRED, because
     the report is built for one location. So the report opens on their first
     branch and offers no "All locations" (4 Oct 2026). */
  const oneAtATime = isBranchScoped && myLocations.length > 1;
  const effectiveOutletId = outletId || (oneAtATime ? myLocations[0].id : "");
  const waitingForBranches = isBranchScoped && !branchesLoaded;
  // A vendor's own login lists its locations as the navbar does (the main
  // store only when it is in scope); staff read them from the chosen kitchen.
  const ownLocations = !vendorId && myLocations.length ? myLocations : null;
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  /**
   * One params object feeds both the report and the CSV, so the file a manager
   * downloads is always the window they are looking at. Memoised on primitives
   * only, which is what keeps the `load` callback below stable enough to sit in
   * a dependency array honestly rather than with a suppression comment.
   */
  const params = useMemo(() => {
    const next = {};
    if (vendorId) next.vendorId = vendorId;
    if (effectiveOutletId) next.outletId = effectiveOutletId;
    const fromIso = dayStartIso(from);
    const toIso = dayEndIso(to);
    if (fromIso) next.from = fromIso;
    if (toIso) next.to = toIso;
    return next;
  }, [vendorId, effectiveOutletId, from, to]);

  const load = useCallback(async () => {
    // A branch member's report waits for their branches, so it never asks
    // for a window the server would refuse.
    if (waitingForBranches) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiKitchenAnalytics(params);
      setReport(res?.data?.data || null);
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.message || "Could not build the kitchen report.");
    } finally {
      setLoading(false);
    }
  }, [params, waitingForBranches]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await apiKitchenExport(params);
      downloadBlob(res.data, `kitchen-tickets-${from}-to-${to}.csv`);
    } catch (err) {
      // The export rides `responseType: "blob"`, so a JSON error body arrives as
      // a Blob and the usual `err.response.data.message` read comes back
      // undefined. Fall through to a plain message instead of showing "[object
      // Blob]" to a manager.
      toast.error(err?.response?.data?.message || "Could not export the tickets.");
    } finally {
      setExporting(false);
    }
  }, [params, from, to]);

  const totals = report?.totals || {};
  const stages = report?.stages || {};
  const distribution = report?.distribution || {};
  const sla = report?.sla || {};
  const handover = report?.handover || {};
  const exceptions = report?.exceptions || {};
  const stations = report?.stations || [];
  const slowestItems = report?.slowestItems || [];
  const operators = report?.operators || [];
  const rejectionReasons = exceptions.rejectionReasons || [];
  const voidReasons = exceptions.voidReasons || [];

  /**
   * The server echoes the filters it actually applied. An empty `vendorIds`
   * means the caller was platform staff who did not narrow to one restaurant,
   * which changes what the operator table means, so read it from the response
   * rather than guessing from the URL.
   */
  const isCrossVendor = (report?.filters?.vendorIds || []).length === 0;
  const timezoneLabel = report?.filters?.timezone || "the market timezone";

  // Horizontal bars, one per stage, labelled on the axis. See the file header
  // for why these are NOT stacked.
  const stageChart = useMemo(() => {
    const banked = report?.stages || {};
    const rows = STAGE_FIELDS.map((field) => ({ ...field, ms: Number(banked?.[field.key]) }));
    return {
      rows,
      hasAny: rows.some((r) => Number.isFinite(r.ms)),
      data: {
        labels: rows.map((r) => r.label),
        datasets: [
          {
            label: "Average minutes",
            data: rows.map((r) => (Number.isFinite(r.ms) ? Number((r.ms / 60000).toFixed(1)) : null)),
            backgroundColor: rows.map((r) => r.colour),
            borderRadius: 5,
            maxBarThickness: 28,
          },
        ],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const row = rows[ctx.dataIndex];
                return `${formatMinutes(row?.ms)} on average, ${row?.anchor}`;
              },
            },
          },
        },
        scales: {
          x: {
            beginAtZero: true,
            title: { display: true, text: "Minutes", color: THEME.textLight, font: { size: 11 } },
            ticks: { color: THEME.textLight, font: { size: 11 } },
            grid: { color: THEME.border },
          },
          y: {
            ticks: { color: THEME.textMid, font: { size: 11.5 } },
            grid: { display: false },
          },
        },
      },
    };
  }, [report]);

  /**
   * The service defaults a null `_id` to ON_TIME when it builds `slaMap`, so
   * the raw `byState` array can carry BOTH a null row and an "ON_TIME" row.
   * Drawing them as two slices would show the same state twice with two
   * different colours. Fold them, weighting the overrun average by count.
   */
  const slaRows = useMemo(() => {
    const merged = new Map();
    (report?.sla?.byState || []).forEach((row) => {
      const state = row?._id || "ON_TIME";
      const count = Number(row?.count) || 0;
      const overrun = Number.isFinite(Number(row?.avgOverrunMs)) ? Number(row.avgOverrunMs) : null;
      const current = merged.get(state);
      if (!current) {
        merged.set(state, { state, count, avgOverrunMs: overrun });
        return;
      }
      const total = current.count + count;
      if (current.avgOverrunMs === null) current.avgOverrunMs = overrun;
      else if (overrun !== null && total > 0) {
        current.avgOverrunMs = (current.avgOverrunMs * current.count + overrun * count) / total;
      }
      current.count = total;
    });

    const known = SLA_ORDER.filter((state) => merged.has(state));
    const unknown = Array.from(merged.keys()).filter((state) => !SLA_ORDER.includes(state));
    return [...known, ...unknown].map((state) => {
      const meta = SLA_META[state] || { label: state, colour: THEME.textLight };
      return { ...merged.get(state), label: meta.label, colour: meta.colour };
    });
  }, [report]);

  const slaChart = useMemo(
    () => ({
      data: {
        labels: slaRows.map((r) => r.label),
        datasets: [
          {
            data: slaRows.map((r) => r.count),
            backgroundColor: slaRows.map((r) => r.colour),
            borderColor: THEME.panel,
            borderWidth: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "58%",
        plugins: {
          legend: {
            position: "bottom",
            labels: { color: THEME.textMid, boxWidth: 12, font: { size: 11.5 }, padding: 12 },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.label}: ${formatCount(ctx.parsed)} tickets`,
            },
          },
        },
      },
    }),
    [slaRows]
  );

  /**
   * `throughput.byHour` is one row per (hour, day of week). The chart asks a
   * simpler question, "when is this kitchen busy", so hours are summed across
   * every day in the window. `throughput.peak` is deliberately NOT recomputed:
   * it is the busiest SINGLE bucket the server found, a different number from
   * the tallest bar here, and the copy says so.
   */
  const throughput = useMemo(() => {
    const buckets = Array.from({ length: 24 }, () => ({ tickets: 0, units: 0, breaches: 0 }));
    let total = 0;
    (report?.throughput?.byHour || []).forEach((row) => {
      const hour = Number(row?.hour);
      if (!Number.isInteger(hour) || hour < 0 || hour > 23) return;
      buckets[hour].tickets += Number(row?.tickets) || 0;
      buckets[hour].units += Number(row?.units) || 0;
      buckets[hour].breaches += Number(row?.breaches) || 0;
      total += Number(row?.tickets) || 0;
    });

    const peak = report?.throughput?.peak || null;
    const peakHour = Number.isInteger(Number(peak?.hour)) ? Number(peak.hour) : null;

    return {
      total,
      peak,
      peakHour,
      buckets,
      data: {
        labels: HOUR_LABELS,
        datasets: [
          {
            label: "Tickets placed",
            data: buckets.map((b) => b.tickets),
            backgroundColor: buckets.map((_, hour) => (hour === peakHour ? THEME.amber : THEME.blue)),
            borderRadius: 4,
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const bucket = buckets[ctx.dataIndex] || { units: 0, breaches: 0 };
                return `${formatCount(ctx.parsed.y)} tickets, ${formatCount(bucket.units)} units, ${formatCount(bucket.breaches)} breaches`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: "Tickets", color: THEME.textLight, font: { size: 11 } },
            ticks: { color: THEME.textLight, font: { size: 11 }, precision: 0 },
            grid: { color: THEME.border },
          },
          x: {
            ticks: { color: THEME.textLight, font: { size: 10 }, maxRotation: 0, autoSkip: true },
            grid: { display: false },
          },
        },
      },
    };
  }, [report]);

  const kpis = useMemo(() => {
    const t = report?.totals || {};
    const s = report?.sla || {};
    const d = report?.distribution || {};
    return [
      {
        key: "tickets",
        label: "Tickets",
        value: formatCount(t.tickets),
        sub: "Every ticket placed in this window, including the ones that were rejected or cancelled.",
        accent: THEME.blue,
      },
      {
        key: "units",
        label: "Units",
        value: formatCount(t.units),
        sub: "Item quantities across those tickets, not the number of order lines.",
        accent: THEME.blue,
      },
      {
        key: "onTime",
        label: "On time",
        value: formatPercent(s.onTimeRate),
        sub: "Share of cooked tickets that finished on time or still inside the promise, judged against the promise saved on each ticket.",
        accent: SLA_META.ON_TIME.colour,
      },
      {
        key: "median",
        label: "Median cooking time",
        value: formatMinutes(d?.prepMs?.p50),
        sub: "Half of cooked tickets finished faster than this, measured from the moment cooking started.",
        accent: THEME.teal,
      },
      {
        key: "p90",
        label: "p90 cooking time",
        value: formatMinutes(d?.prepMs?.p90),
        sub: "Nine in ten cooked tickets finished faster than this. The tail is what a customer remembers.",
        accent: THEME.teal,
      },
      {
        key: "breaches",
        label: "Breaches",
        value: formatCount(s.breached),
        sub: "Cooked tickets that went past the restaurant's own maximum prep time.",
        accent: SLA_META.BREACHED.colour,
      },
      {
        key: "value",
        label: "Ticket value",
        value: formatMoney(t.revenue),
        sub: "Sum of ticket totals in this window. The report carries no currency, so the platform default is shown.",
        accent: THEME.amber,
      },
    ];
  }, [report]);

  const peakDayLabel = DOW_LABELS[Number(throughput.peak?.dayOfWeek)] || "an unrecorded day";
  const peakHourLabel = throughput.peakHour === null ? "an unrecorded hour" : HOUR_LABELS[throughput.peakHour];

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h3 style={S.headerTitle}>Kitchen performance</h3>
        <p style={S.headerSub}>
          Every number below is read back from the duration banked on each ticket at the moment
          the stage ended. Nothing is recalculated from timestamps, so a recall, a clock change
          or a later edit cannot quietly move a figure you have already acted on.
        </p>
      </div>

      {/* ── Filters ───────────────────────────────────────────────────────── */}
      <div style={S.panel}>
        <div style={S.strip}>Report window</div>
        <div style={S.filterRow}>
          <label style={S.field}>
            <span style={S.label}>From</span>
            <input
              type="date"
              style={S.input}
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>

          <label style={S.field}>
            <span style={S.label}>To</span>
            <input
              type="date"
              style={S.input}
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>

          {kitchens.length > 1 ? (
            <label style={S.field}>
              <span style={S.label}>Restaurant</span>
              <AppSelect
                style={S.input}
                value={vendorId}
                onChange={(e) => {
                  const next = e.target.value;
                  setOutletId("");
                  history.replace(
                    next
                      ? `/admin/kitchen-reports?vendorId=${encodeURIComponent(next)}`
                      : "/admin/kitchen-reports"
                  );
                }}
              >
                <option value="">All kitchens</option>
                {kitchens.map((k) => (
                  <option key={k.vendorId} value={k.vendorId}>
                    {k.name}
                  </option>
                ))}
              </AppSelect>
            </label>
          ) : null}

          <label style={S.field}>
            <span style={S.label}>Location</span>
            <AppSelect style={S.input} value={effectiveOutletId} onChange={(e) => setOutletId(e.target.value)}>
              {!oneAtATime && <option value="">All locations</option>}
              {ownLocations ? (
                ownLocations.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.isMain ? "Main store" : l.name}
                  </option>
                ))
              ) : (
                <>
                  {/* A member whose branches leave out the main store is not offered it. */}
                  {selectedKitchen?.includesMain !== false && <option value="main">Main store</option>}
                  {resolvedOutlets.map((outlet) => {
                    const id = outlet?._id || outlet?.id || outlet?.outletId;
                    return (
                      <option key={String(id)} value={String(id)}>
                        {outlet?.name || outlet?.outletName || "Unnamed outlet"}
                      </option>
                    );
                  })}
                </>
              )}
            </AppSelect>
          </label>

          <div style={{ ...S.field, minWidth: 0, justifyContent: "flex-end" }}>
            <button type="button" style={S.button("ghost")} onClick={() => load()} disabled={loading}>
              {loading ? "Loading" : "Refresh"}
            </button>
          </div>

          <div style={{ ...S.field, minWidth: 0, justifyContent: "flex-end" }}>
            <button type="button" style={S.button()} onClick={handleExport} disabled={exporting}>
              {exporting ? "Preparing CSV" : "Export CSV"}
            </button>
          </div>

          {vendorId && kitchens.length <= 1 ? (
            <div style={{ ...S.field, minWidth: 0, justifyContent: "flex-end" }}>
              <span style={S.chip} title="Scoped to one kitchen">
                {selectedKitchen?.name || `Vendor ${vendorId}`}
              </span>
            </div>
          ) : null}
        </div>
        <p style={{ ...S.note, margin: "12px 0 0" }}>
          The CSV is one row per ticket over exactly this window, with the same banked durations
          the charts use. Main store means tickets with no outlet attached.
        </p>
      </div>

      {loading ? <div style={S.panel}>{emptyNote("Building the report.")}</div> : null}

      {!loading && error ? (
        <div style={S.panel}>
          <div style={S.sectionTitle}>The report did not load</div>
          <p style={S.note}>{error}</p>
          <button type="button" style={S.button("ghost")} onClick={() => load()}>
            Try again
          </button>
        </div>
      ) : null}

      {!loading && !error && !report ? (
        <div style={S.panel}>{emptyNote("No report for this window yet.")}</div>
      ) : null}

      {!loading && !error && report ? (
        <>
          {/* ── KPI row ───────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Headline</div>
            <div style={S.kpiGrid}>
              {kpis.map((kpi) => (
                <div key={kpi.key} style={S.kpi(kpi.accent)}>
                  <div style={S.kpiLabel}>{kpi.label}</div>
                  <div style={S.kpiValue}>{kpi.value}</div>
                  <div style={S.kpiSub}>{kpi.sub}</div>
                </div>
              ))}
            </div>
            <p style={{ ...S.note, margin: "14px 0 0" }}>
              Timing figures count only tickets that were actually cooked, which is{" "}
              {formatCount(totals.cooked)} of {formatCount(totals.tickets)} here. Rejected and
              cancelled tickets are excluded from every timing metric and counted under exceptions
              instead, so a kitchen that refuses work cannot improve its own average by doing it.
            </p>
          </div>

          {/* ── Time in stage ─────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Time in stage</div>
            <h4 style={S.sectionTitle}>Where the minutes go</h4>
            <p style={S.note}>
              Each bar is measured from its own anchor and the bars are not stacked. Order to
              accepted and order to cooking started are both measured from the moment the order
              landed, so the second already contains the first. Cooking is measured from the moment
              cooking started, and ready to handed over from the moment the food went up. Adding
              the four together would count the wait before cooking twice.
            </p>

            <div style={S.two}>
              <div>
                {stageChart.hasAny ? (
                  <div style={S.chartBox}>
                    <Bar data={stageChart.data} options={stageChart.options} />
                  </div>
                ) : (
                  emptyNote("No cooked tickets in this window, so there are no stage averages yet.")
                )}
                <div style={S.legendRow}>
                  {stageChart.rows.map((row) => (
                    <span key={row.key}>
                      <span style={S.swatch(row.colour)} />
                      {row.label}: {formatMinutes(row.ms)}
                    </span>
                  ))}
                </div>
                <p style={{ ...S.note, margin: "12px 0 0" }}>
                  Ready to handed over is only banked when a ticket is actually handed off, so it
                  averages over fewer tickets than the rows above it. Average total time in the
                  kitchen is {formatMinutes(stages.avgTotalKitchenMs)}, across{" "}
                  {formatCount(stages.n)} cooked tickets.
                </p>
              </div>

              <div>
                {distribution.sampled ? (
                  <div style={S.warnNote}>
                    Percentiles computed from the most recent {formatCount(distribution.sampleSize)}{" "}
                    of {formatCount(distribution.population)} tickets. The window holds more cooked
                    tickets than the {formatCount(distribution.sampleLimit)} ticket sample cap, so
                    read these as a recent-tickets estimate rather than the whole window.
                  </div>
                ) : (
                  <p style={S.note}>
                    Percentiles cover all {formatCount(distribution.sampleSize)} cooked tickets in
                    this window. No sampling was applied.
                  </p>
                )}
                <div style={S.scroll}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Measure</th>
                        <th style={{ ...S.th, textAlign: "right" }}>p50</th>
                        <th style={{ ...S.th, textAlign: "right" }}>p90</th>
                        <th style={{ ...S.th, textAlign: "right" }}>p99</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Tickets</th>
                      </tr>
                    </thead>
                    <tbody>
                      {PERCENTILE_ROWS.map((row) => {
                        const cell = distribution?.[row.key] || {};
                        return (
                          <tr key={row.key}>
                            <td style={S.td}>{row.label}</td>
                            <td style={S.tdNum}>{formatMinutes(cell.p50)}</td>
                            <td style={S.tdNum}>{formatMinutes(cell.p90)}</td>
                            <td style={S.tdNum}>{formatMinutes(cell.p99)}</td>
                            <td style={S.tdNum}>{formatCount(cell.n)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p style={{ ...S.note, margin: "12px 0 0" }}>
                  Nearest-rank percentiles with no interpolation, so every figure above is a real
                  ticket rather than a number between two of them.
                </p>
              </div>
            </div>
          </div>

          {/* ── SLA ───────────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Service level</div>
            <h4 style={S.sectionTitle}>Against the promise that was live at the time</h4>
            <p style={S.note}>
              Each ticket carries the prep promise it was given when it was placed. Widening the
              promise today does not repaint last week, which is why this section can be trusted
              in a dispute.
            </p>

            <div style={S.two}>
              <div>
                {slaRows.length ? (
                  <div style={S.chartBoxSm}>
                    <Doughnut data={slaChart.data} options={slaChart.options} />
                  </div>
                ) : (
                  emptyNote("No cooked tickets to score in this window.")
                )}
              </div>

              <div>
                <div style={S.kpiGrid}>
                  <div style={S.kpi(SLA_META.BREACHED.colour)}>
                    <div style={S.kpiLabel}>Breach rate</div>
                    <div style={S.kpiValue}>{formatPercent(sla.breachRate)}</div>
                    <div style={S.kpiSub}>
                      {formatCount(sla.breached)} of {formatCount(sla.total)} cooked tickets went
                      past the maximum prep time.
                    </div>
                  </div>
                  <div style={S.kpi(SLA_META.LATE.colour)}>
                    <div style={S.kpiLabel}>Average overrun</div>
                    <div style={S.kpiValue}>{formatDuration(sla.avgOverrunMs)}</div>
                    <div style={S.kpiSub}>
                      How far past the promise the late tickets ran. Measured across the late
                      bucket only, not across every ticket.
                    </div>
                  </div>
                </div>

                <div style={{ ...S.scroll, marginTop: 14 }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>State</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Tickets</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Share</th>
                        <th style={{ ...S.th, textAlign: "right" }}>Average overrun</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slaRows.length ? (
                        slaRows.map((row) => (
                          <tr key={row.state}>
                            <td style={S.td}>
                              <span style={S.swatch(row.colour)} />
                              {row.label}
                            </td>
                            <td style={S.tdNum}>{formatCount(row.count)}</td>
                            <td style={S.tdNum}>
                              {formatPercent(sla.total ? row.count / sla.total : null)}
                            </td>
                            <td style={S.tdNum}>{formatDuration(row.avgOverrunMs)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td style={S.td} colSpan={4}>
                            Nothing scored yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p style={{ ...S.note, margin: "12px 0 0" }}>
                  The on-time headline above counts on time and running close together, because
                  both landed inside the promise. Late and breached are the two that cost a
                  customer.
                </p>
              </div>
            </div>
          </div>

          {/* ── Throughput ────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Throughput</div>
            <h4 style={S.sectionTitle}>When this kitchen is busy</h4>
            <p style={S.note}>
              Tickets summed by hour of day across every day in the window. Hours are grouped in{" "}
              {timezoneLabel}, which is the market clock the tickets were placed against, not your
              browser clock.
            </p>

            {throughput.total > 0 ? (
              <>
                <div style={S.chartBox}>
                  <Bar data={throughput.data} options={throughput.options} />
                </div>
                <div style={S.legendRow}>
                  <span>
                    <span style={S.swatch(THEME.blue)} />
                    Tickets placed in that hour
                  </span>
                  <span>
                    <span style={S.swatch(THEME.amber)} />
                    Hour of the busiest single bucket
                  </span>
                </div>
                {throughput.peak ? (
                  <p style={{ ...S.note, margin: "12px 0 0" }}>
                    Busiest single bucket: {peakDayLabel} at {peakHourLabel}, with{" "}
                    {formatCount(throughput.peak.tickets)} tickets. That is one day and one hour,
                    while the bars are totals across the whole window, so the amber bar is not
                    always the tallest one. Staff the amber hour, and read the tall bars as the
                    steady load.
                  </p>
                ) : null}
              </>
            ) : (
              emptyNote("No tickets were placed in this window.")
            )}
          </div>

          {/* ── Handover ──────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Handover</div>
            <h4 style={S.sectionTitle}>The gap between the kitchen and the road</h4>
            <p style={S.note}>
              Rider wait is the kitchen running late: the rider was already standing there and the
              food was not up. Food wait is the last mile running late: the food went up and nobody
              came for it. Different owners and different fixes, so read them apart rather than
              adding them together.
            </p>

            {Number(handover.n) > 0 ? (
              <>
                <div style={S.kpiGrid}>
                  <div style={S.kpi(SLA_META.AT_RISK.colour)}>
                    <div style={S.kpiLabel}>Average rider wait</div>
                    <div style={S.kpiValue}>{formatDuration(handover.avgRiderWaitMs)}</div>
                    <div style={S.kpiSub}>Rider arrived before the food was ready. Owned by the kitchen.</div>
                  </div>
                  <div style={S.kpi(SLA_META.LATE.colour)}>
                    <div style={S.kpiLabel}>Average food wait</div>
                    <div style={S.kpiValue}>{formatDuration(handover.avgFoodWaitMs)}</div>
                    <div style={S.kpiSub}>Food was ready before anyone came for it. Owned by dispatch.</div>
                  </div>
                  <div style={S.kpi(SLA_META.BREACHED.colour)}>
                    <div style={S.kpiLabel}>Worst food wait</div>
                    <div style={S.kpiValue}>{formatDuration(handover.maxFoodWaitMs)}</div>
                    <div style={S.kpiSub}>The single longest wait on the pass in this window.</div>
                  </div>
                  <div style={S.kpi(THEME.red)}>
                    <div style={S.kpiLabel}>Cold risk</div>
                    <div style={S.kpiValue}>{formatCount(handover.coldRisk)}</div>
                    <div style={S.kpiSub}>
                      Handovers where the food sat for more than 10 minutes. That is the threshold
                      the report is built on.
                    </div>
                  </div>
                </div>
                <p style={{ ...S.note, margin: "14px 0 0" }}>
                  Measured across {formatCount(handover.n)} tickets that recorded a pickup time.
                  Tickets collected at the counter never get one, so they are not in here.
                </p>
              </>
            ) : (
              emptyNote("No tickets in this window recorded a pickup time, so there is no handover picture yet.")
            )}
          </div>

          {/* ── Stations ──────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Stations</div>
            <h4 style={S.sectionTitle}>Load by station</h4>
            <p style={S.note}>
              Counted per item line, so one ticket can appear at several stations. The chip colour
              is the same one the station wears on the live board.
            </p>
            {stations.length ? (
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Station</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Lines</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Units</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Voided</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Average item prep</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stations.map((station) => (
                      <tr key={station.code}>
                        <td style={S.td}>
                          <span style={S.swatch(stationColour(station.code))} />
                          {station.code}
                        </td>
                        <td style={S.tdNum}>{formatCount(station.lines)}</td>
                        <td style={S.tdNum}>{formatCount(station.units)}</td>
                        <td style={S.tdNum}>{formatCount(station.voided)}</td>
                        <td style={S.tdNum}>{formatMinutes(station.avgItemPrepMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              emptyNote("No station activity in this window.")
            )}
          </div>

          {/* ── Slowest items ─────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Menu</div>
            <h4 style={S.sectionTitle}>What holds the line up</h4>
            <p style={S.note}>
              Only items with at least 3 timed observations in this window are listed, because the
              report filters on that. One slow plate on one shift is an anecdote, not a pattern.
            </p>
            {slowestItems.length ? (
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Item</th>
                      <th style={S.th}>Station</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Average prep</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Worst prep</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Times cooked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slowestItems.map((item) => (
                      <tr key={`${item.name}-${item.station || "MAIN"}`}>
                        <td style={S.td}>{item.name}</td>
                        <td style={S.td}>
                          <span style={S.swatch(stationColour(item.station || "MAIN"))} />
                          {item.station || "MAIN"}
                        </td>
                        <td style={S.tdNum}>{formatMinutes(item.avgPrepMs)}</td>
                        <td style={S.tdNum}>{formatMinutes(item.maxPrepMs)}</td>
                        <td style={S.tdNum}>{formatCount(item.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              emptyNote("No item was cooked at least 3 times in this window, so nothing is listed yet.")
            )}
          </div>

          {/* ── Exceptions ────────────────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>Exceptions</div>
            <h4 style={S.sectionTitle}>Work refused, undone and repeated</h4>
            <p style={S.note}>
              These are the tickets kept out of the timing figures. They are counted here instead so
              a kitchen that rejects its way to a clean average still shows the cost somewhere.
            </p>

            <div style={S.kpiGrid}>
              <div style={S.kpi(STATUS_META.REJECTED.colour)}>
                <div style={S.kpiLabel}>{STATUS_META.REJECTED.label}</div>
                <div style={S.kpiValue}>{formatCount(exceptions.rejected)}</div>
                <div style={S.kpiSub}>The kitchen refused the ticket. A reason is required.</div>
              </div>
              <div style={S.kpi(STATUS_META.CANCELLED.colour)}>
                <div style={S.kpiLabel}>{STATUS_META.CANCELLED.label}</div>
                <div style={S.kpiValue}>{formatCount(exceptions.cancelled)}</div>
                <div style={S.kpiSub}>Cancelled upstream of the kitchen, usually on the order.</div>
              </div>
              <div style={S.kpi(THEME.amber)}>
                <div style={S.kpiLabel}>Voids</div>
                <div style={S.kpiValue}>{formatCount(exceptions.voids)}</div>
                <div style={S.kpiSub}>Individual item lines pulled after the ticket was cut.</div>
              </div>
              <div style={S.kpi(THEME.amber)}>
                <div style={S.kpiLabel}>Recalls</div>
                <div style={S.kpiValue}>{formatCount(exceptions.recalls)}</div>
                <div style={S.kpiSub}>A ticket pulled back to an earlier state after a bump.</div>
              </div>
              <div style={S.kpi(THEME.blue)}>
                <div style={S.kpiLabel}>Reopens</div>
                <div style={S.kpiValue}>{formatCount(exceptions.reopens)}</div>
                <div style={S.kpiSub}>A closed ticket brought back onto the board.</div>
              </div>
            </div>

            <div style={{ ...S.two, marginTop: 18 }}>
              <div>
                <div style={S.strip}>Why work was refused</div>
                {rejectionReasons.length ? (
                  <div style={S.scroll}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Reason</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Tickets</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rejectionReasons.map((row) => (
                          <tr key={row.reason}>
                            <td style={S.td}>{row.reason}</td>
                            <td style={S.tdNum}>{formatCount(row.count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  emptyNote("Nothing was rejected in this window.")
                )}
              </div>

              <div>
                <div style={S.strip}>What was voided</div>
                {voidReasons.length ? (
                  <div style={S.scroll}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Item</th>
                          <th style={S.th}>Reason</th>
                          <th style={{ ...S.th, textAlign: "right" }}>Times</th>
                        </tr>
                      </thead>
                      <tbody>
                        {voidReasons.map((row) => (
                          <tr key={`${row.item}-${row.reason}`}>
                            <td style={S.td}>{row.item}</td>
                            <td style={S.td}>{row.reason}</td>
                            <td style={S.tdNum}>{formatCount(row.count)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  emptyNote("Nothing was voided in this window.")
                )}
              </div>
            </div>
          </div>

          {/* ── Who was on the board ──────────────────────────────────────── */}
          <div style={S.panel}>
            <div style={S.strip}>People</div>
            <h4 style={S.sectionTitle}>Who was on the board</h4>
            <p style={S.note}>
              This is who acted in this window, taken from the ticket event log. It answers the
              question that follows every breach review, which is who was on at the time. Read it
              for context on a specific ticket, not as a productivity ranking: a quiet shift and a
              busy one produce very different counts for the same effort.
              {isCrossVendor
                ? " No single restaurant is selected, so this list spans every kitchen with activity in the window."
                : ""}
            </p>
            {operators.length ? (
              <div style={S.scroll}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Name</th>
                      <th style={S.th}>Type</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Actions</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Bumps</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Recalls</th>
                      <th style={{ ...S.th, textAlign: "right" }}>Voids</th>
                      <th style={S.th}>First action</th>
                      <th style={S.th}>Last action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operators.map((operator, index) => {
                      const identity = operator?._id || {};
                      return (
                        <tr key={`${identity.userId || "unknown"}-${index}`}>
                          <td style={S.td}>{identity.name || "Unnamed operator"}</td>
                          <td style={S.td}>{identity.type || "Unrecorded"}</td>
                          <td style={S.tdNum}>{formatCount(operator.actions)}</td>
                          <td style={S.tdNum}>{formatCount(operator.bumps)}</td>
                          <td style={S.tdNum}>{formatCount(operator.recalls)}</td>
                          <td style={S.tdNum}>{formatCount(operator.voids)}</td>
                          <td style={S.td}>{formatDayAndClock(operator.firstAt)}</td>
                          <td style={S.td}>{formatDayAndClock(operator.lastAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              emptyNote("No board activity was recorded in this window.")
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
